import React from 'react';
import { Card, Grid, Text, NumberInput, Divider, Group, Stack, Badge, Box, ThemeIcon, Tabs } from '@mantine/core';
import { IconAdjustments, IconShip, IconUsers, IconTool, IconPercentage, IconPlane } from '@tabler/icons-react';
import type { IConfiguracionState } from '../interfaces';

interface FormularioVariablesGlobalesVistaProps {
  config: IConfiguracionState;
  /** null: campo opcional vacío (topes de margen y descuento). */
  alCambiar: (key: keyof IConfiguracionState, value: number | string | null) => void;
  /** Deshabilita todos los campos (p. ej. cotización aceptada). */
  soloLectura?: boolean;
  /** Moneda local del país (COP, USD). Los viáticos van en esta moneda; el resto de valores en USD. */
  monedaLocal: string;
  /** Solo los países con moneda local distinta del dólar usan TRM (Colombia). */
  usaTrm: boolean;
  /** Si se indica, solo estos campos se pueden editar (p. ej. gerencia: márgenes y descuentos de la cotización). */
  camposEditables?: (keyof IConfiguracionState)[];
  /** Muestra los topes de margen y descuento (solo en la configuración global). */
  mostrarTopes?: boolean;
}


/** Campos de "Variables Globales". Se usa en Configuración y en la configuración de cada cotización. */
export const FormularioVariablesGlobalesVista: React.FC<FormularioVariablesGlobalesVistaProps> = ({
  config,
  alCambiar,
  soloLectura = false,
  monedaLocal,
  usaTrm,
  camposEditables,
  mostrarTopes = false
}) => {
  /** Con camposEditables (perfil de gerencia en una cotización), los demás campos quedan de solo lectura. */
  const bloqueado = (campo: keyof IConfiguracionState) => !!camposEditables && !camposEditables.includes(campo);
  const tope = (campo: keyof IConfiguracionState, etiqueta: string) => (
    <NumberInput min={0} max={100} allowNegative={false} size="xs" label={etiqueta}
      value={config[campo] ?? ''} onChange={(val) => alCambiar(campo, val === '' ? null : Number(val))}
      suffix=" %" hideControls disabled={bloqueado(campo)} />
  );

  return (
  // fieldset nativo: disabled deshabilita de una vez todos los inputs internos.
  <fieldset disabled={soloLectura} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
    <Grid gutter="lg">
      {/* Sección: Variables y Márgenes Globales */}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card shadow="none" p="lg" radius="md" withBorder h="100%">
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              <ThemeIcon variant="light" color="primary" radius="xl" size="md">
                <IconAdjustments size={18} />
              </ThemeIcon>
              <Text fw={600} size="lg">Variables y Márgenes Globales</Text>
            </Group>
            <Badge color="primary" variant="light" radius="xl" style={{ textTransform: "none" }}>Generales</Badge>
          </Group>
          <Divider mb="md" />
          <Stack gap="md">
            {/* Una sola TRM para ascensores y escaleras (solo en países que la usan). */}
            {usaTrm && <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="TRM" value={config.Trm} onChange={(val) => alCambiar('Trm', val)} disabled={bloqueado('Trm')} prefix="$ " suffix=" COP" thousandSeparator hideControls error={config.Trm === null || config.Trm === undefined || String(config.Trm) === ""} />}
            {/* Desktop */}
            <Box visibleFrom="sm">
              <div style={{ display: 'flex', gap: '24px' }}>
                <Box style={{ flex: 1 }}>
                  <Text fw={600} size="sm" mb="sm" ta="center">Ascensores</Text>
                  <Stack gap="xs">
                    <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="Margen Importado" value={config.MargenImportadoAscensor} onChange={(val) => alCambiar('MargenImportadoAscensor', val)} disabled={bloqueado('MargenImportadoAscensor')} suffix=" %" error={config.MargenImportadoAscensor === null || config.MargenImportadoAscensor === undefined || String(config.MargenImportadoAscensor) === ""} />
                    <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="Margen Local" value={config.MargenLocalAscensor} onChange={(val) => alCambiar('MargenLocalAscensor', val)} disabled={bloqueado('MargenLocalAscensor')} suffix=" %" error={config.MargenLocalAscensor === null || config.MargenLocalAscensor === undefined || String(config.MargenLocalAscensor) === ""} />
                    <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="Descuento KONE" value={config.DescuentoAscensor} onChange={(val) => alCambiar('DescuentoAscensor', val)} disabled={bloqueado('DescuentoAscensor')} suffix=" %" error={config.DescuentoAscensor === null || config.DescuentoAscensor === undefined || String(config.DescuentoAscensor) === ""} />
                  </Stack>
                </Box>
                <Divider orientation="vertical" my="lg" />
                <Box style={{ flex: 1 }}>
                  <Text fw={600} size="sm" mb="sm" ta="center">Escaleras</Text>
                  <Stack gap="xs">
                    <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="Margen Importado" value={config.MargenImportadoEscalera} onChange={(val) => alCambiar('MargenImportadoEscalera', val)} disabled={bloqueado('MargenImportadoEscalera')} suffix=" %" error={config.MargenImportadoEscalera === null || config.MargenImportadoEscalera === undefined || String(config.MargenImportadoEscalera) === ""} />
                    <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="Margen Local" value={config.MargenLocalEscalera} onChange={(val) => alCambiar('MargenLocalEscalera', val)} disabled={bloqueado('MargenLocalEscalera')} suffix=" %" error={config.MargenLocalEscalera === null || config.MargenLocalEscalera === undefined || String(config.MargenLocalEscalera) === ""} />
                    <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="Descuento KONE" value={config.DescuentoEscalera} onChange={(val) => alCambiar('DescuentoEscalera', val)} disabled={bloqueado('DescuentoEscalera')} suffix=" %" error={config.DescuentoEscalera === null || config.DescuentoEscalera === undefined || String(config.DescuentoEscalera) === ""} />
                  </Stack>
                </Box>
              </div>
            </Box>

            {/* Mobile */}
            <Box hiddenFrom="sm">
              <Tabs defaultValue="ascensores" variant="outline">
                <Tabs.List mb="sm">
                  <Tabs.Tab value="ascensores">Ascensores</Tabs.Tab>
                  <Tabs.Tab value="escaleras">Escaleras</Tabs.Tab>
                </Tabs.List>
                
                <Tabs.Panel value="ascensores">
                  <Stack gap="xs">
                    <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="Margen Importado" value={config.MargenImportadoAscensor} onChange={(val) => alCambiar('MargenImportadoAscensor', val)} disabled={bloqueado('MargenImportadoAscensor')} suffix=" %" error={config.MargenImportadoAscensor === null || config.MargenImportadoAscensor === undefined || String(config.MargenImportadoAscensor) === ""} />
                    <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="Margen Local" value={config.MargenLocalAscensor} onChange={(val) => alCambiar('MargenLocalAscensor', val)} disabled={bloqueado('MargenLocalAscensor')} suffix=" %" error={config.MargenLocalAscensor === null || config.MargenLocalAscensor === undefined || String(config.MargenLocalAscensor) === ""} />
                    <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="Descuento KONE" value={config.DescuentoAscensor} onChange={(val) => alCambiar('DescuentoAscensor', val)} disabled={bloqueado('DescuentoAscensor')} suffix=" %" error={config.DescuentoAscensor === null || config.DescuentoAscensor === undefined || String(config.DescuentoAscensor) === ""} />
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="escaleras">
                  <Stack gap="xs">
                    <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="Margen Importado" value={config.MargenImportadoEscalera} onChange={(val) => alCambiar('MargenImportadoEscalera', val)} disabled={bloqueado('MargenImportadoEscalera')} suffix=" %" error={config.MargenImportadoEscalera === null || config.MargenImportadoEscalera === undefined || String(config.MargenImportadoEscalera) === ""} />
                    <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="Margen Local" value={config.MargenLocalEscalera} onChange={(val) => alCambiar('MargenLocalEscalera', val)} disabled={bloqueado('MargenLocalEscalera')} suffix=" %" error={config.MargenLocalEscalera === null || config.MargenLocalEscalera === undefined || String(config.MargenLocalEscalera) === ""} />
                    <NumberInput min={0} allowNegative={false} size="xs" withAsterisk required label="Descuento KONE" value={config.DescuentoEscalera} onChange={(val) => alCambiar('DescuentoEscalera', val)} disabled={bloqueado('DescuentoEscalera')} suffix=" %" error={config.DescuentoEscalera === null || config.DescuentoEscalera === undefined || String(config.DescuentoEscalera) === ""} />
                  </Stack>
                </Tabs.Panel>
              </Tabs>
            </Box>
          </Stack>
        </Card>
      </Grid.Col>

      {/* Sección: Logística e Importación */}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card shadow="none" p="lg" radius="md" withBorder h="100%">
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              <ThemeIcon variant="light" color="primary" radius="xl" size="md">
                <IconShip size={18} />
              </ThemeIcon>
              <Text fw={600} size="lg">Logística e Importación</Text>
            </Group>
            <Badge color="primary" variant="light" radius="xl" style={{ textTransform: "none" }}>USD</Badge>
          </Group>
          <Divider mb="md" />
          <Stack gap="md">
            <Grid align="flex-end">
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Flete Marítimo (Ascensores)" description="Costo por contenedor" value={config.FleteMaritimoAscensor} onChange={(val) => alCambiar('FleteMaritimoAscensor', val)} disabled={bloqueado('FleteMaritimoAscensor')} prefix="$ " suffix=" USD" thousandSeparator hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Flete Marítimo (Escaleras)" description="Costo por contenedor" value={config.FleteMaritimoEscalera} onChange={(val) => alCambiar('FleteMaritimoEscalera', val)} disabled={bloqueado('FleteMaritimoEscalera')} prefix="$ " suffix=" USD" thousandSeparator hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="FOB Unitario" value={config.GastosPuertoFob} onChange={(val) => alCambiar('GastosPuertoFob', val)} disabled={bloqueado('GastosPuertoFob')} prefix="$ " suffix=" USD" thousandSeparator hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="SIA" description="Valor fijo por equipo" value={config.SiaBase} onChange={(val) => alCambiar('SiaBase', val)} disabled={bloqueado('SiaBase')} prefix="$ " suffix=" USD" thousandSeparator hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Montacargas" description="En puerto" value={config.MontacargasPuerto} onChange={(val) => alCambiar('MontacargasPuerto', val)} disabled={bloqueado('MontacargasPuerto')} prefix="$ " suffix=" USD" thousandSeparator hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Flete Nacional (Ascensores)" value={config.FleteNacionalAscensor} onChange={(val) => alCambiar('FleteNacionalAscensor', val)} disabled={bloqueado('FleteNacionalAscensor')} prefix="$ " suffix=" USD" thousandSeparator hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Flete Nacional (Escaleras)" value={config.FleteNacionalEscalera} onChange={(val) => alCambiar('FleteNacionalEscalera', val)} disabled={bloqueado('FleteNacionalEscalera')} prefix="$ " suffix=" USD" thousandSeparator hideControls />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>
      </Grid.Col>

      {/* Sección: Mano de Obra */}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card shadow="none" p="lg" radius="md" withBorder h="100%">
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              <ThemeIcon variant="light" color="primary" radius="xl" size="md">
                <IconUsers size={18} />
              </ThemeIcon>
              <Text fw={600} size="lg">Mano de Obra</Text>
            </Group>
            <Badge color="primary" variant="light" radius="xl" style={{ textTransform: "none" }}>M.O.D</Badge>
          </Group>
          <Divider mb="md" />
          <Stack gap="md">
            <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Hora Hombre" value={config.TarifaHoraHombre} onChange={(val) => alCambiar('TarifaHoraHombre', val)} disabled={bloqueado('TarifaHoraHombre')} prefix="$ " suffix=" USD" thousandSeparator hideControls />
            <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Horas Día" value={config.HorasDiaInstalacion} onChange={(val) => alCambiar('HorasDiaInstalacion', val)} disabled={bloqueado('HorasDiaInstalacion')} hideControls />
            <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Técnicos" value={config.CantidadTecnicos} onChange={(val) => alCambiar('CantidadTecnicos', val)} disabled={bloqueado('CantidadTecnicos')} hideControls />
          </Stack>
        </Card>
      </Grid.Col>

      {/* Sección: Mantenimiento */}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card shadow="none" p="lg" radius="md" withBorder h="100%">
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              <ThemeIcon variant="light" color="primary" radius="xl" size="md">
                <IconTool size={18} />
              </ThemeIcon>
              <Text fw={600} size="lg">Costos de Mantenimiento Incluido</Text>
            </Group>
            <Badge color="primary" variant="light" radius="xl" style={{ textTransform: "none" }}>Garantía / Local</Badge>
          </Group>
          <Divider mb="md" />
          <Stack gap="md">
            <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="MTTO. Free Ofrecido (Ascensores)" description="Costo mensual" value={config.MantenimientoAscensorMensual} onChange={(val) => alCambiar('MantenimientoAscensorMensual', val)} disabled={bloqueado('MantenimientoAscensorMensual')} prefix="$ " suffix=" USD" thousandSeparator hideControls />
            <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="MTTO. Free Ofrecido (Escaleras)" description="Costo mensual" value={config.MantenimientoEscaleraMensual} onChange={(val) => alCambiar('MantenimientoEscaleraMensual', val)} disabled={bloqueado('MantenimientoEscaleraMensual')} prefix="$ " suffix=" USD" thousandSeparator hideControls />
          </Stack>
        </Card>
      </Grid.Col>

      {/* Costos Fijos Locales */}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card shadow="none" p="lg" radius="md" withBorder h="100%">
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              <ThemeIcon variant="light" color="primary" radius="xl" size="md">
                <IconTool size={18} />
              </ThemeIcon>
              <Text fw={600} size="lg">Costos Fijos Locales</Text>
            </Group>
            <Badge color="primary" variant="light" radius="xl" style={{ textTransform: "none" }}>Fijos</Badge>
          </Group>
          <Divider mb="md" />
          <Stack gap="md">
            <Grid align="flex-end">
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Baterías EBD (Ascensores)" value={config.BateriasEbd} onChange={(val) => alCambiar('BateriasEbd', val)} disabled={bloqueado('BateriasEbd')} prefix="$ " suffix=" USD" hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Desembalaje" value={config.Desembalaje} onChange={(val) => alCambiar('Desembalaje', val)} disabled={bloqueado('Desembalaje')} prefix="$ " suffix=" USD" thousandSeparator="," hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Montacargas para Trasiegos" value={config.MontacargasInterno} onChange={(val) => alCambiar('MontacargasInterno', val)} disabled={bloqueado('MontacargasInterno')} prefix="$ " suffix=" USD" thousandSeparator="," hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Insumos de Instalación" value={config.InsumosInstalacion} onChange={(val) => alCambiar('InsumosInstalacion', val)} disabled={bloqueado('InsumosInstalacion')} prefix="$ " suffix=" USD" thousandSeparator="," hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Certificación" value={config.CertificacionNtc} onChange={(val) => alCambiar('CertificacionNtc', val)} disabled={bloqueado('CertificacionNtc')} prefix="$ " suffix=" USD" thousandSeparator="," hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Transformador" description="Por kW" value={config.TransformadorKw} onChange={(val) => alCambiar('TransformadorKw', val)} disabled={bloqueado('TransformadorKw')} prefix="$ " suffix=" USD" thousandSeparator="," hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Imprevistos (Ascensores)" value={config.Imprevistos} onChange={(val) => alCambiar('Imprevistos', val)} disabled={bloqueado('Imprevistos')} prefix="$ " suffix=" USD" thousandSeparator="," hideControls />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Herramientas (Escaleras)" value={config.Herramientas} onChange={(val) => alCambiar('Herramientas', val)} disabled={bloqueado('Herramientas')} prefix="$ " suffix=" USD" thousandSeparator="," hideControls />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>
      </Grid.Col>

      {/* Impuestos y Recargos */}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card shadow="none" p="lg" radius="md" withBorder h="100%">
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              <ThemeIcon variant="light" color="primary" radius="xl" size="md">
                <IconPercentage size={18} />
              </ThemeIcon>
              <Text fw={600} size="lg">Impuestos y Seguros</Text>
            </Group>
            <Badge color="primary" variant="light" radius="xl" style={{ textTransform: "none" }}>Recargos</Badge>
          </Group>
          <Divider mb="md" />
          <Stack gap="md">
            <Grid align="flex-end">
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="SIA" description="Sobre el costo del equipo" value={config.PorcentajeSia} onChange={(val) => alCambiar('PorcentajeSia', val)} disabled={bloqueado('PorcentajeSia')} suffix=" %" decimalScale={2} />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Seguro" description="Sobre el costo del equipo" value={config.PorcentajeSeguro} onChange={(val) => alCambiar('PorcentajeSeguro', val)} disabled={bloqueado('PorcentajeSeguro')} suffix=" %" decimalScale={2} />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="FIC" value={config.PorcentajeFic} onChange={(val) => alCambiar('PorcentajeFic', val)} disabled={bloqueado('PorcentajeFic')} suffix=" %" decimalScale={2} />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="CIF" value={config.PorcentajeCif} onChange={(val) => alCambiar('PorcentajeCif', val)} disabled={bloqueado('PorcentajeCif')} suffix=" %" decimalScale={2} />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="UPME" value={config.PorcentajeUpme} onChange={(val) => alCambiar('PorcentajeUpme', val)} disabled={bloqueado('PorcentajeUpme')} suffix=" %" decimalScale={2} />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Base Gravable UPME" value={config.PorcentajeBaseGravableUpme} onChange={(val) => alCambiar('PorcentajeBaseGravableUpme', val)} disabled={bloqueado('PorcentajeBaseGravableUpme')} suffix=" %" decimalScale={2} />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Pólizas" value={config.PorcentajePolizas} onChange={(val) => alCambiar('PorcentajePolizas', val)} disabled={bloqueado('PorcentajePolizas')} suffix=" %" decimalScale={2} />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Comisión" value={config.PorcentajeComisionImportacion} onChange={(val) => alCambiar('PorcentajeComisionImportacion', val)} disabled={bloqueado('PorcentajeComisionImportacion')} suffix=" %" decimalScale={2} />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>
      </Grid.Col>

      {/* Viáticos y Desplazamientos */}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card shadow="none" p="lg" radius="md" withBorder h="100%">
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              <ThemeIcon variant="light" color="primary" radius="xl" size="md">
                <IconPlane size={18} />
              </ThemeIcon>
              <Text fw={600} size="lg">Viáticos y Desplazamientos</Text>
            </Group>
            <Badge color="primary" variant="light" radius="xl" style={{ textTransform: "none" }}>Aplica fuera ciudad base</Badge>
          </Group>
          <Divider mb="md" />
          
          <Grid mb="md" align="flex-end">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Viáticos" description="Por día y por técnico" value={config.ValorViaticoDiaPersona} onChange={(val) => alCambiar('ValorViaticoDiaPersona', val)} disabled={bloqueado('ValorViaticoDiaPersona')} prefix="$ " suffix={` ${monedaLocal}`} thousandSeparator hideControls />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Valor Promedio Vuelo" value={config.ValorPromedioVuelo} onChange={(val) => alCambiar('ValorPromedioVuelo', val)} disabled={bloqueado('ValorPromedioVuelo')} prefix="$ " suffix={` ${monedaLocal}`} thousandSeparator hideControls />
            </Grid.Col>
            <Grid.Col span={12}>
              <NumberInput min={0} allowNegative={false} size="xs" withAsterisk label="Costo Local Valor Transporte por Día" description="Incluye los traslados de ida y vuelta entre casa y aeropuerto, y entre aeropuerto y hotel." value={config.CostoLocalValorTransportePorDia} onChange={(val) => alCambiar('CostoLocalValorTransportePorDia', val)} disabled={bloqueado('CostoLocalValorTransportePorDia')} prefix="$ " suffix={` ${monedaLocal}`} thousandSeparator hideControls />
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      {/* Topes de margen y descuento de las cotizaciones: solo en la configuración global (los define el administrador).
          Media columna, al lado de Viáticos y Desplazamientos. */}
      {mostrarTopes && (
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card shadow="none" p="lg" radius="md" withBorder h="100%">
            <Group justify="space-between" mb="xs">
              <Group gap="xs">
                <ThemeIcon variant="light" color="primary" radius="xl" size="md">
                  <IconPercentage size={18} />
                </ThemeIcon>
                <Text fw={600} size="lg">Topes de la Cotización</Text>
              </Group>
              <Badge color="primary" variant="light" radius="xl" style={{ textTransform: "none" }}>Márgenes y descuentos</Badge>
            </Group>
            <Divider mb="md" />
            <Text size="xs" c="dimmed" mb="sm">Límites para los márgenes y descuentos que se editan en cada cotización. Vacío = sin tope.</Text>
            <Grid mb="md" align="flex-end">
              <Grid.Col span={{ base: 12, sm: 6 }}>{tope('MargenMinimo', 'Margen mínimo')}</Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>{tope('MargenMaximo', 'Margen máximo')}</Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>{tope('DescuentoMinimo', 'Descuento mínimo')}</Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>{tope('DescuentoMaximo', 'Descuento máximo')}</Grid.Col>
            </Grid>
          </Card>
        </Grid.Col>
      )}
    </Grid>
  </fieldset>
  );
};
